import { type CSSProperties, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { boardColor, type CalendarOccurrence, useCalendar } from '@/entities/board';
import { addDays, todayIn } from '@/shared/lib/calendar';
import { formatCalendarDate } from '@/shared/lib/datetime';
import { Icons, Popover } from '@/shared/ui';

import { CalendarSourcePicker } from './CalendarSourcePicker';
import { EventPreview } from './EventPreview';
import { OccurrenceDialog } from './OccurrenceDialog';
import styles from './HomeCalendarPreview.module.css';
import {
  type CalendarEvent,
  eventsOnDay,
  localNow,
  timeRangeOnDay,
  toEvents,
  toOccurrenceEvents,
  withPhases,
} from './model';
import { knownHidden, useSharedSources, workspaceNamesOf } from './sources';
import { StateTag } from './StateTag';

/** The days a preview covers. */
const DAYS = 7;

interface BoardRef {
  id: string;
  name: string;
  color: string | null;
  workspace_id: string | null;
  workspace_name?: string | null;
}

interface Props {
  /** Boards the viewer can see, for the names and colours of the sources. */
  boards: readonly BoardRef[];
  timeZone: string | null;
  locale: string;
  /** Where the complete global calendar lives. */
  calendarPath: string;
  /** Opens the original card (`boardId` is the board it lives on). */
  onOpenCard: (cardId: string, boardId: string) => void;
}

const QUERY = {
  includeCompleted: true,
  includeOverdue: true,
  includeUndated: false,
  search: '',
  labelIds: [] as string[],
  boardIds: [] as string[],
};

/**
 * The compact calendar of the home page: the next seven days of the GLOBAL calendar as a short
 * agenda (time range, title, source board), with ‹ › to move by a week, a click for the event
 * preview and a link to the complete calendar. It is a preview of the same server data
 * (`/me/boards/calendar`): it can show nothing the person has no access to. Upcoming repeats of a
 * series open the shared repeat editor where the person may change them; everything else is read-only.
 */
export function HomeCalendarPreview({ boards, timeZone, locale, calendarPath, onOpenCard }: Props) {
  const { t } = useTranslation();
  const today = todayIn(timeZone);
  const [weeks, setWeeks] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  // The editor of an upcoming repeat: the same dialog (one / this and following / series) as in
  // the full calendar.
  const [occurrence, setOccurrence] = useState<CalendarOccurrence | null>(null);
  const start = addDays(today, weeks * DAYS);
  const end = addDays(start, DAYS - 1);
  // The same boards as the full calendar (shared per user). No boards selected must mean NO
  // events: an empty `boardIds` is "every board" for the server, so nothing is requested then.
  const shared = useSharedSources();
  const hidden = useMemo(() => knownHidden(shared.hidden, boards), [shared.hidden, boards]);
  const shown = boards.length - hidden.length;
  const nothingShown = boards.length > 0 && shown === 0;
  const boardIds = useMemo(
    () => (hidden.length > 0 ? boards.filter((b) => !hidden.includes(b.id)).map((b) => b.id) : []),
    [boards, hidden],
  );
  const calendar = useCalendar({ kind: 'all' }, { ...QUERY, boardIds, start, end }, !nothingShown);

  const sources = useMemo(() => new Map(boards.map((board) => [board.id, board])), [boards]);
  const workspaceNames = useMemo(() => workspaceNamesOf(boards), [boards]);
  // Filtered here as well: cached or placeholder data of an earlier selection must not show.
  const events = useMemo(
    () =>
      nothingShown
        ? []
        : withPhases(
            [
              ...toEvents(calendar.data?.cards ?? []),
              ...toOccurrenceEvents(calendar.data?.occurrences ?? []),
            ].filter((event) => sources.has(event.boardId) && !hidden.includes(event.boardId)),
            localNow(timeZone),
          ),
    [calendar.data, sources, hidden, nothingShown, timeZone],
  );
  const days = Array.from({ length: DAYS }, (_, index) => addDays(start, index)).flatMap((day) => {
    const items = eventsOnDay(events, day);
    return items.length > 0 ? [{ day, items }] : [];
  });
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const sourceName = (boardId: string) => {
    const board = sources.get(boardId);
    return board ? [board.name, board.workspace_name].filter(Boolean).join(' · ') : '';
  };
  // The server lists the boards where the person may create (= change) tasks; the repeat editor
  // is offered only there, and the server re-checks every change.
  const editableIds = calendar.data?.creatable_board_ids;
  const canEditBoard = (boardId: string) => editableIds?.includes(boardId) ?? false;
  const opened = openId ? events.find((event) => event.id === openId) : undefined;
  const timeOf = (event: CalendarEvent, day: string) =>
    event.kind === 'deadline'
      ? // a due marker, not a time range (a flag and the word "Due" are drawn beside it)
        event.allDay
        ? t('calendar.due')
        : t('calendar.dueAt', { time: event.start.slice(11, 16) })
      : event.allDay
        ? t('calendar.allDay')
        : (timeRangeOnDay(event, day) ?? event.start.slice(11, 16));

  return (
    <section
      className={styles.panel}
      aria-labelledby="home-calendar-title"
      data-testid="home-calendar"
    >
      <header className={styles.head}>
        <h2 id="home-calendar-title" className={styles.title}>
          {t('calendar.global.homeTitle')}
        </h2>
        <Link to={calendarPath} className={styles.open} data-testid="open-global-calendar">
          {t('calendar.global.openFull')} <Icons.arrowRight />
        </Link>
      </header>
      <div className={styles.navRow}>
        <span className={styles.nav} role="group" aria-label={t('calendar.global.home')}>
          <button
            type="button"
            className={styles.navButton}
            aria-label={t('calendar.global.previous')}
            disabled={weeks === 0}
            onClick={() => {
              setWeeks((current) => Math.max(0, current - 1));
            }}
          >
            <Icons.chevronLeft />
          </button>
          <span className={styles.range} data-testid="home-calendar-range">
            {formatCalendarDate(start, locale)} – {formatCalendarDate(end, locale)}
          </span>
          <button
            type="button"
            className={styles.navButton}
            aria-label={t('calendar.global.next')}
            onClick={() => {
              setWeeks((current) => current + 1);
            }}
          >
            <Icons.chevronRight />
          </button>
        </span>
        {boards.length > 1 && (
          <Popover
            label={t('calendar.sources.title')}
            width={340}
            alignEnd
            trigger={(props, open) => (
              <button
                {...props}
                type="button"
                className={styles.sources}
                data-open={open || undefined}
                data-testid="home-calendar-sources"
              >
                {nothingShown
                  ? t('calendar.sources.buttonNone')
                  : hidden.length === 0
                    ? t('calendar.sources.buttonAll')
                    : t('calendar.sources.buttonCount', { shown, total: boards.length })}
                <Icons.chevronDown />
              </button>
            )}
          >
            <div className={styles.picker}>
              <CalendarSourcePicker
                boards={boards}
                workspaceNames={workspaceNames}
                hiddenBoardIds={hidden}
                onChange={shared.remember}
              />
            </div>
          </Popover>
        )}
      </div>
      {nothingShown ? (
        <p className={styles.empty} data-testid="home-calendar-none">
          {t('calendar.sources.nothingSelected')}
        </p>
      ) : calendar.isPending ? (
        <p className={styles.empty}>{t('common.loading')}</p>
      ) : calendar.isError ? (
        <p className={styles.empty} role="alert">
          {t('errors.UNKNOWN')}
        </p>
      ) : days.length === 0 ? (
        <p className={styles.empty}>{t('calendar.global.homeNothing')}</p>
      ) : (
        <ul className={styles.days}>
          {days.map(({ day, items }) => (
            <li key={day} className={styles.day} data-day={day}>
              <h3 className={styles.dayHead}>
                <span className={day === today ? styles.today : undefined}>
                  {weekday.format(new Date(`${day}T00:00:00Z`))}, {formatCalendarDate(day, locale)}
                </span>
              </h3>
              <ul className={styles.list}>
                {items.map((event) => {
                  const board = sources.get(event.boardId);
                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        className={styles.item}
                        data-testid="home-calendar-item"
                        data-kind={event.kind}
                        onClick={() => {
                          setOpenId(event.id);
                        }}
                      >
                        <span
                          className={styles.bar}
                          style={
                            { '--bar': board ? boardColor(board) : undefined } as CSSProperties
                          }
                          aria-hidden
                        />
                        <span className={styles.text}>
                          <span className={styles.titleRow}>
                            <span
                              className={styles.itemTitle}
                              data-done={event.card.completed_at !== null ? 'true' : undefined}
                            >
                              {event.card.title}
                            </span>
                            <StateTag event={event} />
                          </span>
                          <span className={styles.meta}>
                            <span className={styles.time}>
                              {event.kind === 'deadline' && <Icons.flag />}
                              {timeOf(event, day)}
                            </span>
                            {sourceName(event.boardId) && (
                              <>
                                <span aria-hidden>·</span>
                                <span className={styles.source}>{sourceName(event.boardId)}</span>
                              </>
                            )}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {opened && (
        <EventPreview
          item={{ event: opened }}
          source={sourceName(opened.boardId)}
          workspaceId={null}
          locale={locale}
          canEdit={opened.occurrence !== undefined && canEditBoard(opened.boardId)}
          onClose={() => {
            setOpenId(null);
          }}
          onOpenCard={() => {
            setOpenId(null);
            onOpenCard(opened.id, opened.boardId);
          }}
          onEditOccurrence={() => {
            if (opened.occurrence && canEditBoard(opened.boardId)) {
              setOccurrence(opened.occurrence);
            } else {
              onOpenCard(opened.occurrence?.source_card_id ?? opened.id, opened.boardId);
            }
            setOpenId(null);
          }}
          onOpenSeriesSource={(cardId) => {
            setOpenId(null);
            onOpenCard(cardId, opened.boardId);
          }}
        />
      )}
      {occurrence && (
        <OccurrenceDialog
          occurrence={occurrence}
          canEdit={canEditBoard(occurrence.board_id)}
          onClose={() => {
            setOccurrence(null);
          }}
          onOpenCard={(cardId) => {
            setOccurrence(null);
            onOpenCard(cardId, occurrence.board_id);
          }}
        />
      )}
    </section>
  );
}
